import { EProjectType, type IActionParameter } from "@kuika/kuika-cl-model";

/**
 * The actions panel's shared visibility rule for a parameter, read by the panel's generic parameter
 * list, the symbols groups that list their own parameters, and the "optional parameters" menu. The
 * action picker's preview reads it through `action-parameter-surface.ts`, which adds what each special
 * symbols group singles out.
 */

/**
 * Whether the actions panel lists the parameter as a row. A required parameter always does; an
 * optional one only once it has a value or the user switched it on from the optional-parameters menu.
 */
export function isActionParameterShownInPanel(parameter: IActionParameter): boolean {
  if (parameter.isOptionalParameter !== true) {
    return true;
  }
  if (parameter.symbol?.fixedValue) {
    return true;
  }
  return parameter.isExternalVisible === true;
}

/**
 * Whether the optional-parameters menu offers the parameter. `hideFromOptionalParameters` marks the
 * optional parameters the platform sets on the user's behalf (a JS action's `runOnServer`,
 * Navigate's `disableSmartExecutePageInit`) — never offered, so never something the user edits.
 */
export function isOptionalActionParameterOffered(parameter: IActionParameter): boolean {
  return parameter.isOptionalParameter === true && parameter.hideFromOptionalParameters !== true;
}

/** What the optional-parameters menu knows about where the action lives. */
export interface IOptionalParameterMenuContext {
  /** Unknown (e.g. in the action picker's preview) reads as "not mobile". */
  projectType?: EProjectType;
}

/**
 * Whether the optional-parameters menu lists an offered parameter in this context. `eventType` is
 * never picked from the menu (Set Value Of draws it behind its "Trigger Events" switch), and
 * `triggerParentAppGoBack` only means something to a web parent app hosting micro apps. The menu's
 * state-dependent rule (Search's `sessionId` waits for RAG Search) stays in `OptionalParameterList`.
 */
export function isOptionalActionParameterSelectableInMenu(
  parameter: IActionParameter,
  context: IOptionalParameterMenuContext
): boolean {
  if (parameter.name === "eventType") {
    return false;
  }
  return !(context.projectType === EProjectType.Mobile && parameter.name === "triggerParentAppGoBack");
}
